import axios from 'axios';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './DemoCourse.css';
import { INSTRUCTOR_URL } from '../../Services/Services';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DemoCourse = () => {
  const [demoFile, setDemoFile] = useState(null);
  const [courseContent, setCourseContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isUploadedDemo, setIsUploadedDemo] = useState(false);
  const instructorId = localStorage.getItem('userId');
  const navigate = useNavigate();
  const [demoTitle, setDemoTitle] = useState('');
  const [previousWorkExperience, setPreviousWorkExperience] = useState('');
  const [totalYearsExperience, setTotalYearsExperience] = useState('');
  const [teachingDomains, setTeachingDomains] = useState('');
  const [hoursAvailablePerWeek, setHoursAvailablePerWeek] = useState('');
  const [preferredSchedule, setPreferredSchedule] = useState('');
  const [totalCourseHours, setTotalCourseHours] = useState('');

  const handleDemoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setDemoFile(file);
      toast.dismiss();
      toast.success(`Demo file selected: ${file.name}`);
    }
  };

  const handleContentFileChange = (event) => {
    const file = event.target.files[0];
    toast.dismiss();
    if (file) {
      setCourseContent(file);
      toast.success(`Content file selected: ${file.name}`);
    }
  };

  useEffect(() => {
    if (instructorId) {
      setLoading(true);

      toast.dismiss();
      const fetchApprovalStatus = async () => {
        try {
          const response = await fetch(`${INSTRUCTOR_URL}/approval-status/${instructorId}`);
          const data = await response.json();
          const status = data[0];
          setIsUploadedDemo(status === 'APPROVED' || status === 'PENDING');
          if (data[0] === "APPROVED") navigate('/');
        } catch (error) {
          console.error('Error fetching approval status:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchApprovalStatus();
    }
  }, [instructorId, navigate]);

  const handleUpload = async (event) => {
    event.preventDefault();

    toast.dismiss();
    if (!demoFile) {
      toast.error('Please select a demo video file to upload.');
      return;
    }

    if (!courseContent) {
      toast.error('Please select a course content file to upload.');
      return;
    }

    const formData = new FormData();
    formData.append('demoFile', demoFile);
    formData.append('courseContent', courseContent);
    formData.append('demoTitle', demoTitle);
    formData.append('previousWorkExperience', previousWorkExperience);
    formData.append('totalYearsExperience', totalYearsExperience);
    formData.append('teachingDomains', teachingDomains);
    formData.append('totalCourseHours', totalCourseHours);
    formData.append('hoursAvailablePerWeek', hoursAvailablePerWeek);
    formData.append('preferredSchedule', preferredSchedule);

    setLoading(true);
    toast.dismiss();
    const toastId = toast.success('Uploading your demo and course content...', {
      autoClose: false,
    });

    try {
      await axios.post(
        `${INSTRUCTOR_URL}/${instructorId}/upload-demo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      toast.dismiss(toastId);

      toast.dismiss();
      setLoading(false);
      toast.success('Upload successful! Please wait until the admin approves your demo.');
      setIsUploadedDemo(true);
    } catch (error) {
      toast.dismiss();
      toast.dismiss(toastId);
      setLoading(false);
      const errorMessage = error?.response?.data || 'Error uploading files. Please try again.';
      toast.error(errorMessage);
    }
    finally {
      setLoading(false);
      toast.dismiss(toastId);
    }
  };

  return (
    <div className='demo-course-mainContainer'>
      <div className="demo-course-container">
        {isUploadedDemo ? (
          <div className="demo-review-message">
            <h3>Your demo is under review</h3>
            <p>Thank you for submitting your demo video and course content. Our team is reviewing your submission.</p>
            <p>If approved, you will be able to proceed with uploading additional course materials.</p>
          </div>
        ) : (
          <>
            <h2>Upload Your Demo Video and Course Content</h2>
            <div className="demo-video-info-container-2">

              <video
                controls
                autoPlay
                src="https://poc14.s3.ap-south-1.amazonaws.com/AI-interview-recordings/INSTRUCTIONS/20250310_124005_video.mp4"
                type="video/mp4"
                className="demo-video-component-2"
                muted={false}
              />
            </div>
            <form onSubmit={handleUpload} className="upload-form">
              <label htmlFor="demoFile">Select Your Demo Video:</label>
              <p className="instructions">The demo video should be approximately 5 minutes long and showcase your teaching style and expertise.</p>
              <input
                type="file"
                id="demoFile"
                accept="video/*"
                onChange={handleDemoFileChange}
                className="file-input"
              />

              <label htmlFor="courseContent">Upload Course Content File:</label>
              <p className="instructions">Please upload detailed course content files (PDF, DOC, DOCX) that include all necessary materials for the course you are creating.</p>
              <input
                type="file"
                id="courseContent"
                onChange={handleContentFileChange}
                className="file-input"
                accept=".pdf, .doc, .docx"
              />

              <div className="field-group">
                <label htmlFor="totalCourseHours">Total Course Duration (in hours, e.g., 20 hours)</label>
                <input type='number' id='totalCourseHours' value={totalCourseHours} onChange={(e) => setTotalCourseHours(e.target.value)} className='input-field' />

                <label htmlFor="demoTitle">Demo Title</label>
                <input
                  type="text"
                  id="demoTitle"
                  value={demoTitle}
                  onChange={(e) => setDemoTitle(e.target.value)}
                  className="input-field"
                />
              </div>

              <div className="additional-fields">
                <div className="field-group">
                  <label htmlFor="previousWorkExperience">Previous Work Experience:</label>
                  <input
                    type="text"
                    id="previousWorkExperience"
                    value={previousWorkExperience}
                    onChange={(e) => setPreviousWorkExperience(e.target.value)}
                    className="input-field"
                  />
                </div>

                <div className="field-group">
                  <label htmlFor="totalYearsExperience">Total Years of Experience:</label>
                  <input
                    type="number"
                    id="totalYearsExperience"
                    value={totalYearsExperience}
                    onChange={(e) => setTotalYearsExperience(e.target.value)}
                    min="0"
                    className="input-field"
                  />
                </div>
                <div className="field-group">
                  <label htmlFor="teachingDomains">Teaching Domains:</label>
                  <input
                    type="text"
                    id="teachingDomains"
                    value={teachingDomains}
                    onChange={(e) => setTeachingDomains(e.target.value)}
                    className="input-field"
                  />
                </div>


                <div className="field-group">
                  <label htmlFor="hoursAvailablePerWeek">Hours Available Per Week:</label>
                  <input
                    type="number"
                    id="hoursAvailablePerWeek"
                    value={hoursAvailablePerWeek}
                    onChange={(e) => setHoursAvailablePerWeek(e.target.value)}
                    className="input-field"
                  />
                </div>
                <div className="field-group">
                  <label htmlFor="preferredSchedule">Preferred Schedule:</label>
                  <input
                    type="text"
                    id="preferredSchedule"
                    value={preferredSchedule}
                    onChange={(e) => setPreferredSchedule(e.target.value)}
                    className="input-field"
                  />
                </div>
              </div>

              <button type="submit" className="cta-home-button">Upload Files</button>
            </form>
            {loading && (
              <div className="loading-overlay">
                <ClipLoader color="#333333" loading={loading} size={50} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DemoCourse;
